import React, { useEffect, useState } from "react";
import {
  UserAlert,
  createAlert,
  fetchDepartamentos,
  fetchSecciones,
  fetchTiposDocumento,
  updateAlert,
} from "../services/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

import { Loader2, Save, X } from "lucide-react";

interface AlertFormProps {
  username: string;
  alert: UserAlert | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const AlertForm: React.FC<AlertFormProps> = ({
  username,
  alert,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<UserAlert>({
    username: username,
    alert_name: "",
    seccion_codigo: "",
    departamento_codigo: "",
    tipo_documento: "",
    busqueda: "",
    texto: "",
    fecha_desde: "",
    fecha_hasta: "",
    frequency: "daily",
  });

  const [secciones, setSecciones] = useState<any[]>([]);
  const [departamentos, setDepartamentos] = useState<any[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [seccionesData, departamentosData, tiposDocumentoData] =
          await Promise.all([
            fetchSecciones(),
            fetchDepartamentos(),
            fetchTiposDocumento(),
          ]);

        setSecciones(seccionesData);
        setDepartamentos(departamentosData);
        setTiposDocumento(tiposDocumentoData);

        // Si hay una alerta para editar, cargar sus datos
        if (alert) {
          setFormData({
            ...alert,
            username: username, // Asegurarse de que el username sea el correcto
          });
        }
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError(
          "No se pudieron cargar los datos necesarios. Por favor, inténtalo de nuevo más tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [alert, username]);

  // Manejar cambios en los campos del formulario
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Manejar cambios en los selects
  const handleSelectChange = (name: string, value: string) => {
    // Convert 'all' to empty string for API compatibility
    const finalValue = value === "all" ? "" : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  // Enviar el formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      if (!formData.alert_name.trim()) {
        setError("El nombre de la alerta es obligatorio");
        return;
      }

      if (alert?.id) {
        // Actualizar alerta existente
        await updateAlert(alert.id, formData);
      } else {
        // Crear nueva alerta
        await createAlert(formData);
      }

      onSuccess();
    } catch (err: any) {
      console.error("Error al guardar la alerta:", err);
      setError(
        err.message ||
          "Error al guardar la alerta. Por favor, inténtalo de nuevo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Cargando...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label htmlFor="alert_name">Nombre de la alerta *</Label>
          <Input
            id="alert_name"
            name="alert_name"
            value={formData.alert_name}
            onChange={handleChange}
            placeholder="Ej: Leyes de Educación"
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            Un nombre descriptivo para identificar esta alerta
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="seccion_codigo">Sección</Label>
            <Select
              value={
                formData.seccion_codigo === "" ? "all" : formData.seccion_codigo
              }
              onValueChange={(value) =>
                handleSelectChange("seccion_codigo", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una sección" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las secciones</SelectItem>
                {secciones.map((seccion) => (
                  <SelectItem key={seccion.codigo} value={seccion.codigo}>
                    {seccion.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="departamento_codigo">Departamento</Label>
            <Select
              value={
                formData.departamento_codigo === ""
                  ? "all"
                  : formData.departamento_codigo
              }
              onValueChange={(value) =>
                handleSelectChange("departamento_codigo", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un departamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los departamentos</SelectItem>
                {departamentos.map((departamento) => (
                  <SelectItem
                    key={departamento.codigo}
                    value={departamento.codigo}
                  >
                    {departamento.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="tipo_documento">Tipo de documento</Label>
          <Select
            value={
              formData.tipo_documento === "" ? "all" : formData.tipo_documento
            }
            onValueChange={(value) =>
              handleSelectChange("tipo_documento", value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona un tipo de documento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {tiposDocumento.map((tipo) => (
                <SelectItem key={tipo.tipo} value={tipo.tipo}>
                  {tipo.label} - {tipo.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="texto">Texto a buscar</Label>
          <Input
            id="texto"
            name="texto"
            value={formData.texto || ""}
            onChange={handleChange}
            placeholder="Texto a buscar en el título"
          />
          <p className="text-sm text-gray-500 mt-1">
            Buscar en el título (ignorando mayúsculas)
          </p>
        </div>

        <div>
          <Label htmlFor="frequency">Frecuencia de notificaciones</Label>
          <Select
            value={formData.frequency || "daily"}
            onValueChange={(value) => handleSelectChange("frequency", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona la frecuencia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Diaria</SelectItem>
              <SelectItem value="weekly">Semanal</SelectItem>
              <SelectItem value="monthly">Mensual</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500 mt-1">
            Con qué frecuencia quieres recibir notificaciones de esta alerta
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {alert ? "Actualizar Alerta" : "Crear Alerta"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default AlertForm;
